/**
 * src/components/map/mapTooltipPlacement.ts
 *
 * Where a hover card goes so it neither leaves the map nor lands on a
 * button.
 *
 * This is the placement arithmetic the country tooltip has been doing
 * inline, lifted out unchanged so a second kind of tooltip can be
 * positioned the same way. It was worth extracting on its own before
 * anything about the card's appearance was touched: it is the only part
 * of a tooltip that is pure arithmetic, so it is the only part that can
 * be pinned down by tests, and "the tooltip opened on top of the
 * hamburger menu" is the failure that actually gets reported.
 *
 * Everything is in map-container pixels — the same space Mapbox reports
 * `e.point` in — because the card is an absolutely positioned sibling of
 * the map rather than a Mapbox `Popup`, and the two spaces coincide.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Top-left corner, in the same pixel space as the anchor. */
export interface Corner {
  left: number;
  top: number;
}

export interface MapTooltipPlacement {
  anchor: Point;
  /** Map container size. */
  viewport: { width: number; height: number };
  /** The card's own size. Height may be approximate; it is used only for avoidance. */
  size: { width: number; height: number };
  /** Screen furniture the card must not cover. */
  avoid?: readonly Rect[];
  /** Minimum gap to the map edge. */
  pad?: number;
  /** Where the card sits relative to the cursor before any correction. */
  offset?: Point;
}

const DEFAULT_PAD = 8;
/** To the right of the cursor and slightly above it. */
const DEFAULT_OFFSET: Point = { x: 20, y: -20 };

/**
 * The control clusters on the main map.
 *
 * Measured from the right edge, because that is where they are pinned:
 * the hamburger and action buttons at the top, and the spatial
 * Inspector button at the bottom. Both are given a little more room
 * than they occupy so a card that merely touches them still moves.
 */
export function mainMapControlZones(
  viewport: { width: number; height: number },
): Rect[] {
  const { width, height } = viewport;
  return [
    // Top-right nav: hamburger, actions, and the start of any dropdown.
    { left: width - 250, top: 0, right: width, bottom: 92 },
    // Bottom-right Inspector button.
    { left: width - 220, top: height - 70, right: width, bottom: height },
  ];
}

function overlaps(a: Rect, b: Rect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/**
 * Top-left corner for a card anchored at `anchor`.
 *
 * The order of correction matters and is the order it was written in:
 *
 *  1. Prefer the right of the cursor, so the card does not sit under the
 *     hand that is pointing at the thing.
 *  2. If that covers a control cluster, flip to the left of the cursor —
 *     but only if the flipped card still fits, since a card half off the
 *     left edge is worse than one over a button.
 *  3. Clamp into the map.
 *  4. If clamping shoved it back under something, drop it clear.
 */
export function placeMapTooltip(input: MapTooltipPlacement): Corner {
  const { anchor, viewport, size } = input;
  const pad = input.pad ?? DEFAULT_PAD;
  const offset = input.offset ?? DEFAULT_OFFSET;
  const avoid = input.avoid ?? [];

  const maxLeft = viewport.width - size.width - pad;
  const maxTop = Math.max(pad, viewport.height - size.height - pad);

  const hits = (left: number, top: number): Rect[] => {
    const card: Rect = {
      left, top, right: left + size.width, bottom: top + size.height,
    };
    return avoid.filter((zone) => overlaps(card, zone));
  };

  let left = anchor.x + offset.x;
  let top = anchor.y + offset.y;

  if (hits(left, top).length) {
    const flipped = anchor.x - size.width - offset.x;
    if (flipped >= pad) left = flipped;
  }

  left = Math.max(pad, Math.min(left, maxLeft));
  top = Math.max(pad, Math.min(top, maxTop));

  const stillHitting = hits(left, top);
  if (!stillHitting.length) return { left, top };

  // Clamping put it back over something. The inline version this came
  // from only ever tried moving *down*, which is why hovering near the
  // right edge could still land the card on the bottom-right Inspector
  // button: below the button there is no room, and nothing tried
  // anywhere else. So try each way out, nearest first, and take the
  // first that actually clears.
  const highest = Math.min(...stillHitting.map((zone) => zone.top));
  const lowest = Math.max(...stillHitting.map((zone) => zone.bottom));
  const leftmost = Math.min(...stillHitting.map((zone) => zone.left));

  const candidates: Corner[] = [
    { left, top: lowest + 4 },                          // below it
    { left, top: highest - size.height - 4 },            // above it
    { left: leftmost - size.width - 4, top },            // beside it
  ];

  for (const candidate of candidates) {
    const fits = candidate.left >= pad && candidate.left <= maxLeft
      && candidate.top >= pad && candidate.top <= maxTop;
    if (fits && !hits(candidate.left, candidate.top).length) return candidate;
  }

  // Nowhere clears — a viewport too small to hold both the card and the
  // controls. Stay on the map and accept the overlap.
  return { left, top: Math.min(Math.max(top, lowest + 4), maxTop) };
}
